import Analytics from '@withlocals/withlocals-analytics';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/merge';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import { OneSignalService } from 'services/OneSignal';
import { isCordova } from 'utils/platform';

import { ServiceProps } from './Service';

/**
 * Defines the structure of the processed push notification object
 * that the PushMessageHandler works with. This object originates from
 * OneSignal's additionalData.
 */
export interface PushMessage {
  link?: string;
  type?: string;
  id?: string;
  city?: string;
  country?: string;
  templateName?: string;
  language?: string;
  url?: string; // For legacy type='url' notifications
}

interface PushMessageHandlerService {
  startPush: () => void;
  pushEvents$: Observable<PushMessage>;
}

interface PushProviders {
  oneSignalService: OneSignalService;
}

function handleOpenedNotificationAnalytics(notification: PushMessage): void {
  if (notification.templateName) {
    Analytics.track(
      'App',
      'PushMessageOpen',
      `${notification.templateName}_${notification.language || 'unknown'}`,
    );
  } else {
    console.warn(
      'PushMessageHandler: templateName not found for Analytics on open event.',
      notification,
    );
  }
}

function tryNavigate(
  fluxActions: ServiceProps['flux']['actions'],
  path: string | undefined,
  navigationType: string,
): boolean {
  if (!path) {
    console.log(
      `PushMessageHandler: Navigation not attempted for type "${navigationType}" due to empty path.`,
    );
    return false;
  }

  console.log(`PushMessageHandler: Navigating via ${navigationType}:`, path);
  try {
    fluxActions.menu.goTo(path);
    console.log(`PushMessageHandler: Called menu.goTo(${path}) successfully.`);
    return true;
  } catch (e) {
    console.error(`PushMessageHandler: Error calling menu.goTo(${path}):`, e);
    return false;
  }
}

function tryHandleSpecificAction(
  fluxActions: ServiceProps['flux']['actions'],
  notification: PushMessage,
): boolean {
  if (notification.type === 'booking' && notification.id) {
    console.log(
      'PushMessageHandler: Handling type "booking", ID:',
      notification.id,
    );
    fluxActions.menu.toSelectedSchedule(notification.id);
    return true;
  }
  if (notification.type === 'message' && notification.id) {
    console.log(
      'PushMessageHandler: Handling type "message", ID:',
      notification.id,
    );
    fluxActions.menu.toSelectedMessage(notification.id);
    return true;
  }
  if (
    notification.type === 'search' &&
    notification.city &&
    notification.country
  ) {
    console.log(
      'PushMessageHandler: Handling type "search", City:',
      notification.city,
      'Country:',
      notification.country,
    );
    fluxActions.menu.findThingsToDoIn(notification.country, notification.city);
    return true;
  }
  return false;
}

/**
 * Initializes push notification handling logic.
 */
export function PushMessageHandler(
  props: ServiceProps,
  { oneSignalService }: PushProviders,
): PushMessageHandlerService {
  if (!oneSignalService) {
    console.warn(
      'PushMessageHandler: oneSignalService is not defined at init time. Will lazy-initialize when startPush is called.',
    );
    return {
      startPush: () => {
        if (isCordova()) {
          console.log('PushMessageHandler: Lazy-initializing OneSignal');
          const { OneSignal } = require('services/OneSignal');
          const service = OneSignal();
          service.requestPushPermissions();
        } else {
          console.log(
            'PushMessageHandler: Not a Cordova environment. Skipping OneSignal push permission request.',
          );
        }
      },
      pushEvents$: new Observable<PushMessage>(),
    };
  }

  const startPush = () => {
    if (isCordova()) {
      // Lazy-initialize OneSignal if it wasn't available at module load time
      // Refactor once possible to ensure OneSignal is always initialized before this
      if (!oneSignalService || !oneSignalService.requestPushPermissions) {
        const { OneSignal } = require('services/OneSignal');
        const service = OneSignal();
        service.requestPushPermissions();
      } else {
        oneSignalService.requestPushPermissions();
      }
    } else {
      console.log(
        'PushMessageHandler: Not a Cordova environment. Skipping OneSignal push permission request.',
      );
    }
  };

  const notificationOpens$: Observable<PushMessage> =
    oneSignalService.notificationOpensData$;

  notificationOpens$.subscribe(
    (notification) => {
      console.log(
        'PushMessageHandler: Notification Opened (processed object):',
        notification,
      );

      handleOpenedNotificationAnalytics(notification);

      // Priority 1: New navigation link
      if (tryNavigate(props.flux.actions, notification.link, 'new link')) {
        return;
      }

      // Priority 2: Specific typed actions that don't use the generic 'goTo'
      if (tryHandleSpecificAction(props.flux.actions, notification)) {
        return;
      }

      // Priority 3: Legacy 'url' type navigation
      if (
        notification.type === 'url' &&
        tryNavigate(props.flux.actions, notification.url, "legacy type 'url'")
      ) {
        return;
      }

      console.log(
        'PushMessageHandler: No specific handler executed for this opened notification.',
        notification,
      );
    },
    (e) =>
      console.error(
        'PushMessageHandler: Error in notificationOpens$ subscription:',
        e,
      ),
  );

  const notificationReceive$: Observable<PushMessage> = Observable.merge(
    oneSignalService.notificationReceivesData$,
  );

  notificationReceive$
    .filter(
      (msg): msg is PushMessage & { templateName: string } =>
        !!msg.templateName,
    )
    .map(({ templateName, language }) => ({
      templateName,
      language: language || 'unknown',
    }))
    .subscribe(
      (event) => {
        Analytics.track(
          'App',
          'PushMessageReceive',
          `${event.templateName}_${event.language}`,
        );
      },
      (e) =>
        console.error(
          'PushMessageHandler: Error in notificationReceive$ subscription:',
          e,
        ),
    );

  return {
    startPush,
    pushEvents$: Observable.merge(notificationReceive$, notificationOpens$),
  };
}
